import React, {useState} from 'react';
import styled from 'styled-components';
import {Button, ComboBox, Detail, EMPHASIS, Text} from '@forecast-it/design-system';
import {FEATURE_FLAG_CARD_BORDER, FEATURE_FLAG_NEUTRAL_FILL} from '../featureFlagSurface';
import {CompanyPickerOption} from './featureFlagLists';

const Bar = styled.div`
	display: flex;
	align-items: flex-end;
	gap: 10px;
	padding: 16px 18px;
	background: ${FEATURE_FLAG_NEUTRAL_FILL};
	border-bottom: 1px solid ${FEATURE_FLAG_CARD_BORDER};
`;

const PickerBlock = styled.div`
	display: flex;
	flex-direction: column;
	gap: 4px;
	flex: 1;
	min-width: 0;
	max-width: 420px;
`;

const OptionRow = styled.span`
	display: flex;
	align-items: baseline;
	gap: 8px;
`;

export interface CompanyPickerBarProps {
	label: string;
	options: CompanyPickerOption[];
	onAdd: (companyId: string) => void;
}

export function CompanyPickerBar({label, options, onAdd}: CompanyPickerBarProps): JSX.Element {
	const [selectedCompanyId, setSelectedCompanyId] = useState<string | null>(null);
	// Bumped after a successful add so the ComboBox remounts and clears its typed text and selection,
	// leaving the control ready for the next pick — the same reset the flag list's own company picker
	// uses for its check-company control.
	const [resetToken, setResetToken] = useState(0);

	const handleAdd = (): void => {
		if (selectedCompanyId == null) {
			return;
		}
		onAdd(selectedCompanyId);
		setSelectedCompanyId(null);
		setResetToken(token => token + 1);
	};

	return (
		<Bar>
			<PickerBlock>
				{/* `defaultItems`, not `items`: a controlled items list is never run through the design
				    system's built-in text filter, so typing would not narrow the list. See
				    FeatureFlagsToolbar.tsx's check-company control for the same trap. */}
				<ComboBox
					key={resetToken}
					label={label}
					width={ComboBox.WIDTH.FLEX}
					menuTrigger="focus"
					placeholder={'Search companies'}
					onChange={(companyId: string | null) => setSelectedCompanyId(companyId)}
					defaultItems={options}
				>
					{(item: CompanyPickerOption) => (
						// `textValue` carries only the name: folding the user count into it would make
						// every company match a search for "users".
						<ComboBox.Item textValue={item.name}>
							<OptionRow>
								<Text>{item.name}</Text>
								<Detail color="medium">{item.usersLabel}</Detail>
							</OptionRow>
						</ComboBox.Item>
					)}
				</ComboBox>
			</PickerBlock>
			<Button
				emphasis={EMPHASIS.HIGH}
				onPress={handleAdd}
				disabled={selectedCompanyId == null}
				data-cy="company-picker-add"
			>
				Add
			</Button>
		</Bar>
	);
}
